/**
 * Inquiry detail modal component
 * Shows customer info, inquiry message, and allows updating inquiry status
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Mail, Phone, MapPin, Package, Loader2, ShoppingCart } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InquiryStatusBadge } from './InquiryStatusBadge';
import { CopyButton } from './CopyButton';
import { InquiryRow, InquiryStatus } from '@/modules/marketplace/types/inquiries.types';
import { toast } from 'sonner';

interface InquiryDetailModalProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  inquiry?: InquiryRow | null;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  isUpdating?: boolean;
  onStatusUpdate?: (status: InquiryStatus, estimatedQuote?: number) => Promise<void>;
  onClose?: () => void;
}

export function InquiryDetailModal({
  isOpen = false,
  onOpenChange,
  inquiry,
  isLoading = false,
  isError = false,
  error = null,
  isUpdating = false,
  onStatusUpdate,
  onClose,
}: InquiryDetailModalProps) {
  const navigate = useNavigate();
  const [isContactingCustomer, setIsContactingCustomer] = useState(false);
  const [isCheckingCustomer, setIsCheckingCustomer] = useState(false);

  const handleContactedClick = async () => {
    if (!inquiry) return;

    try {
      setIsContactingCustomer(true);
      await onStatusUpdate?.(InquiryStatus.CONTACTED);
      toast.success('Inquiry status updated to Contacted');
      handleClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update inquiry status';
      console.error('Status update error:', err);
      toast.error(errorMessage);
    } finally {
      setIsContactingCustomer(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!inquiry) return;

    setIsCheckingCustomer(true);

    try {
      // Import getAllBuyers dynamically to avoid circular dependencies
      const { getAllBuyers } = await import('@/services/buyerAPI');

      // Fetch all customers and check if inquiry customer exists
      const response = await getAllBuyers({ page: 0, size: 1000 });
      const customers = response?.data?.content || response?.content || [];

      // Find customer by phone number (primary matching criteria)
      const existingCustomer = customers.find((customer: any) => {
        const customerPhone = customer.phone || customer.primaryPhone;
        const inquiryPhone = inquiry.customerPhone;

        // Normalize phone numbers (remove spaces, dashes, etc.)
        const normalizePhone = (phone: string) => phone.replace(/[\s\-\(\)]/g, '');

        return customerPhone && inquiryPhone &&
               normalizePhone(customerPhone) === normalizePhone(inquiryPhone);
      });

      const baseParams: Record<string, string> = {
        inquiryId: inquiry.id,
        customerName: inquiry.customerName,
        customerPhone: inquiry.customerPhone,
        ...(inquiry.customerEmail && { customerEmail: inquiry.customerEmail }),
        ...(inquiry.product && { productName: inquiry.product }),
        ...(inquiry.quantity && { quantity: inquiry.quantity.toString() }),
        ...(inquiry.quantityUnit && { quantityUnit: inquiry.quantityUnit }),
        ...(inquiry.location && { location: inquiry.location }),
      };

      if (existingCustomer) {
        // CASE B: Existing Customer - Navigate directly to order creation
        console.log('✅ Customer found:', existingCustomer);
        toast.success(`Customer found: ${existingCustomer.name || existingCustomer.companyName}`);

        const params = new URLSearchParams({
          ...baseParams,
          customerId: existingCustomer.id.toString(),
        });

        navigate(`/sales/orders/new?${params.toString()}`);
      } else {
        // CASE A: New Customer - Navigate to customer creation page
        console.log('ℹ️ Customer not found, routing to creation page');
        toast.info('New customer - please create customer profile first');

        const params = new URLSearchParams({
          openForm: 'true',
          returnTo: encodeURIComponent('/sales/orders/new'),
          ...baseParams,
        });

        // Navigate to customer management page with form open
        navigate(`/sales/customers?${params.toString()}`);
      }

      handleClose();
    } catch (err) {
      console.error('Error checking customer:', err);
      toast.error('Failed to check customer. Please try again.');
    } finally {
      setIsCheckingCustomer(false);
    }
  };

  const handleClose = () => {
    onOpenChange?.(false);
    onClose?.();
  };

  if (!inquiry && !isLoading && !isError) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Inquiry Details</DialogTitle>
          <DialogDescription>
            View customer information and manage inquiry status
          </DialogDescription>
        </DialogHeader>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {/* Error State */}
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error?.message || 'Failed to load inquiry details'}
            </AlertDescription>
          </Alert>
        )}

        {/* Content */}
        {inquiry && (
          <div className="space-y-6">
            {/* Product and Status */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-600">Product</p>
                  <h3 className="text-lg font-semibold">{inquiry.product}</h3>
                </div>
                <InquiryStatusBadge status={inquiry.status} />
              </div>
            </div>

            <Separator />

            {/* Customer Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Customer Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Name */}
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="text-base font-medium">{inquiry.customerName}</p>
                </div>

                {/* Email */}
                <div>
                  <p className="text-sm text-gray-600 mb-2">Email</p>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <p className="text-base font-mono break-all">{inquiry.customerEmail}</p>
                    <CopyButton
                      text={inquiry.customerEmail}
                      label="Copy"
                      icon={true}
                      size="sm"
                      variant="ghost"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <p className="text-sm text-gray-600 mb-2">Phone</p>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <p className="text-base font-mono">{inquiry.customerPhone}</p>
                    <CopyButton
                      text={inquiry.customerPhone}
                      label="Copy"
                      icon={true}
                      size="sm"
                      variant="ghost"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Separator />

            {/* B2B Inquiry Details (Quantity & Location) */}
            {(inquiry.quantity || inquiry.location) && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader>
                  <CardTitle className="text-base text-blue-800">B2B Quote Request</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Quantity */}
                  {inquiry.quantity && (
                    <div className="flex items-center gap-3">
                      <Package className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="text-sm text-gray-600">Quantity Requested</p>
                        <p className="text-lg font-semibold text-blue-800">
                          {inquiry.quantity} {inquiry.quantityUnit || 'units'}
                        </p>
                      </div>
                    </div>
                  )}
                  {/* Location */}
                  {inquiry.location && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm text-gray-600">Delivery Location</p>
                        <p className="text-base font-medium text-green-800">{inquiry.location}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Separator />

            {/* Inquiry Message */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Additional Requirements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 mb-2">Message</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{inquiry.message}</p>
                </div>
              </CardContent>
            </Card>

            {/* Timeline Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Timeline</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Created</span>
                  <span className="font-medium">
                    {new Date(inquiry.createdAt).toLocaleString('en-IN')}
                  </span>
                </div>
                {inquiry.contactedAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Contacted</span>
                    <span className="font-medium">
                      {new Date(inquiry.contactedAt).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
                {inquiry.convertedAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Converted</span>
                    <span className="font-medium">
                      {new Date(inquiry.convertedAt).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
                {inquiry.closedAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Closed</span>
                    <span className="font-medium">
                      {new Date(inquiry.closedAt).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex justify-between items-center pt-4 border-t">
          <div className="flex gap-2">
            {/* Show "Mark as Contacted" button only when status is NOT CONTACTED */}
            {inquiry?.status !== InquiryStatus.CONTACTED && (
              <Button
                onClick={handleContactedClick}
                disabled={isContactingCustomer}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isContactingCustomer && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Mark as Contacted
              </Button>
            )}

            {/* Show "Create Order" button only when status is CONTACTED */}
            {inquiry?.status === InquiryStatus.CONTACTED && (
              <Button
                onClick={handleCreateOrder}
                disabled={isCheckingCustomer}
                className="bg-green-600 hover:bg-green-700"
              >
                {isCheckingCustomer && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {!isCheckingCustomer && <ShoppingCart className="h-4 w-4 mr-2" />}
                {isCheckingCustomer ? 'Checking Customer...' : 'Create Order'}
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
